"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState } from "react";
import { Phone, MessageCircle, Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
import { initTracking } from "@/lib/tracking";
function RecursiveSubmenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="flex flex-col gap-1 min-w-[200px] p-2 bg-popover text-popover-foreground rounded-md shadow-md border border-border">
      {items.map((item, i) => <li key={item.id} data-index={i} className="relative group/sub">
          {item.children && item.children.length > 0 ? <div className="flex flex-col">
              <span className="px-3 py-1.5 text-sm font-medium text-foreground flex items-center justify-between">
                {item.label}
                <ChevronDown className="size-3.5 -rotate-90 rtl:rotate-90 text-muted-foreground" />
              </span>
              <div className="pe-2 ps-3 border-s border-border my-1">
                <RecursiveSubmenu items={item.children} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="block px-3 py-1.5 text-sm font-normal text-foreground hover:bg-accent hover:text-accent-foreground rounded-sm transition-colors">
                {item.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-3">
        <div className="h-8 w-18 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-18 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-18 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-18 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  const items = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "الرئيسية",
    href: "/"
  }, {
    id: "2",
    label: "خدماتنا",
    href: "/خدماتنا"
  }, {
    id: "3",
    label: "المدونة",
    href: "/المدونة"
  }, {
    id: "4",
    label: "تواصل معنا",
    href: "/تواصل-معنا"
  }];
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {items.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="text-foreground hover:text-primary transition-colors text-sm font-medium">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <RecursiveSubmenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileRecursiveItems({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={`flex flex-col gap-1 ${depth > 0 ? "ps-4 border-s border-border mt-1" : ""}`}>
      {items.map((item, i) => <li key={item.id} data-index={i} className="flex flex-col">
          <DrawerClose asChild>
            <Link to={item.href || "#"} className="py-2 px-3 text-base font-medium text-foreground hover:text-primary transition-colors rounded-md hover:bg-muted">
              {item.label}
            </Link>
          </DrawerClose>
          {item.children && item.children.length > 0 && <MobileRecursiveItems items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2 p-4">
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
        <div className="h-9 w-full bg-muted animate-pulse rounded-md" />
      </div>;
  }
  const items = menuItems && menuItems.length > 0 ? menuItems : [{
    id: "1",
    label: "الرئيسية",
    href: "/"
  }, {
    id: "2",
    label: "خدماتنا",
    href: "/خدماتنا"
  }, {
    id: "3",
    label: "المدونة",
    href: "/المدونة"
  }, {
    id: "4",
    label: "تواصل معنا",
    href: "/تواصل-معنا"
  }];
  return <MobileRecursiveItems items={items} />;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    initTracking();
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll handler for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <section id="الهيدر" data-nav="light" dir="rtl" className={`sticky top-0 z-50 w-full bg-background/95 backdrop-blur-md transition-shadow duration-300 border-b border-border ${scrolled ? "shadow-sm" : ""}`}>
        <div className="max-w-[1240px] mx-auto px-4 md:px-6 h-20 flex items-center justify-between gap-4">
          {/* Right slot: Site Logo alone without adjacent text or shapes */}
          <div className="flex items-center shrink-0">
            <Link to="/">
              <WvcLogo className="h-12 w-auto" />
            </Link>
          </div>

          {/* Center slot: Main Navigation Menu */}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="18">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {/* Left slot: Contact actions and mobile drawer trigger */}
          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Phone call quick link */}
            <a href={PHONE_TEL_HREF} className="hidden lg:inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-foreground hover:text-primary transition-colors px-2.5 py-1.5 rounded-md border border-border bg-card">
              <Phone className="size-3.5 text-primary" />
              <span>اتصال: {PHONE_LOCAL}</span>
            </a>

            {/* WhatsApp Direct Action Button */}
            <Button asChild variant="default" size="sm" className="bg-primary text-secondary-foreground hover:bg-primary/90 font-medium px-3 sm:px-4 h-9 shadow-xs">
              <Link to={WHATSAPP_HREF} newTab>
                <MessageCircle className="size-4" />
                <span>واتساب مباشر</span>
              </Link>
            </Button>

            {/* Mobile Navigation Drawer */}
            <div className="md:hidden">
              <SafeHydrate><Drawer direction="right">
                <DrawerTrigger asChild>
                  <Button variant="outline" size="icon" className="size-9 border-border bg-card text-foreground" aria-label="فتح القائمة">
                    <Menu className="size-5" />
                  </Button>
                </DrawerTrigger>
                <DrawerContent className="p-4 flex flex-col justify-between max-w-xs">
                  <div>
                    <DrawerHeader className="px-0 pt-0 pb-4 border-b border-border flex items-center justify-between">
                      <DrawerTitle className="text-base font-semibold text-foreground">
                        القائمة
                      </DrawerTitle>
                      <DrawerClose asChild>
                        <Button variant="ghost" size="icon" className="size-8" aria-label="إغلاق القائمة">
                          <X className="size-4" />
                        </Button>
                      </DrawerClose>
                    </DrawerHeader>

                    <div className="py-4">
                      <MenuProvider menu_id="18">
                        <MobileMenuConsumer />
                      </MenuProvider>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border flex flex-col gap-2">
                    <a href={PHONE_TEL_HREF} className="flex items-center justify-center gap-2 text-sm font-medium text-foreground py-2 px-3 rounded-md border border-border bg-card hover:bg-muted transition-colors">
                      <Phone className="size-4 text-primary" />
                      <span>اتصال: {PHONE_LOCAL}</span>
                    </a>
                  </div>
                </DrawerContent>
              </Drawer></SafeHydrate>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Action Button Group */}
      <div dir="rtl" className="fixed top-24 left-5 z-50 flex flex-col gap-3 pointer-events-auto">
        {/* WhatsApp Floating Button */}
        <Button asChild size="icon" className="size-14 rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white shadow-lg transition-transform hover:scale-105 active:scale-95">
          <Link to={WHATSAPP_HREF} newTab aria-label="تواصل عبر واتساب">
            <MessageCircle className="size-7 text-white" />
          </Link>
        </Button>

        {/* Phone Call Floating Button */}
        <Button asChild size="icon" className="size-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95">
          <a href={PHONE_TEL_HREF} aria-label="اتصال هاتفي">
            <Phone className="size-7 text-white" />
          </a>
        </Button>
      </div>
    </>;
}